import React, { useEffect, useMemo, useState } from "react";
import useUserStore from "../stores/useUserStore";
import useFleetStore from "../stores/useFleetStore";
import { useApi } from "../apiHelpers";
import { useToast } from "../components/Toast/ToastContext";
import { bulkAddFlags } from "../utils/flag-utils";

import { useConfirm } from "../components/ConfirmModal/ConfirmContext";
import { DataTable } from "../components/table/DataTable";
import { Alert } from '../components/ui/Alert';
import { buttonClass } from '../components/ui/Button';
import EmptyState from '../components/ui/EmptyState';
import Field from '../components/ui/Field';
import LogoSpinner from '../components/ui/LogoSpinner';
import { StatTile } from '../components/ui/Panel';
// Icons
const FlagIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
    <path d="M14.778.085A.5.5 0 0 1 15 .5V8a.5.5 0 0 1-.314.464L14.5 8l.186.464-.003.001-.006.003-.023.009a12 12 0 0 1-.397.15c-.264.095-.631.223-1.047.35-.816.252-1.879.523-2.71.523-.847 0-1.548-.28-2.158-.525l-.028-.01C7.68 8.71 7.14 8.5 6.5 8.5c-.7 0-1.638.23-2.437.477A20 20 0 0 0 3 9.342V15.5a.5.5 0 0 1-1 0V.5a.5.5 0 0 1 1 0v.282c.226-.079.496-.17.79-.26C4.606.272 5.67 0 6.5 0c.84 0 1.524.277 2.121.519l.043.018C9.286.788 9.828 1 10.5 1c.7 0 1.638-.23 2.437-.477a20 20 0 0 0 1.349-.476l.019-.007.004-.002h.001"/>
  </svg>
);

// Columns stay stable across renders: what changes per keystroke reaches the cells through the table's meta.
const COLUMNS = [
  {
    id: "serial",
    header: "Serial",
    accessorFn: (r) => r.id,
    cell: ({ row }) => <span className="font-mono text-primary">{row.original.id}</span>,
  },
  {
    id: "flag",
    header: "Flag",
    enableSorting: false,
    cell: ({ row, table }) => {
      const m = table.options.meta;
      const id = row.original.id;
      const currentFlag = m.currentFlags[id];
      return currentFlag ? (
        <span className="text-on-surface-variant italic">{currentFlag}</span>
      ) : (
        <Field
          type="text"
          placeholder="Enter flag reason..."
          aria-label={`Flag reason for ${id}`}
          value={m.inputs[id] || ""}
          onChange={(e) => m.onInput(id, e.target.value)}
          maxLength={500}
        />
      );
    },
  },
];

const getRowKey = (r) => String(r.idx);

const BulkFlagAdd = ({ droneIds, closeModal, onDirtyChange }) => {
  const api = useApi();
  const toast = useToast();
  const confirm = useConfirm();
  const user = useUserStore((state) => state.user);
  const flags = useFleetStore((state) => state.flags);
  const refreshFleet = useFleetStore((state) => state.refreshFleet);
  const [flagInputs, setFlagInputs] = useState({});
  const [error, setError] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  // Any reason typed into a row is work the modal would throw away.
  const dirty = Object.values(flagInputs).some((v) => v.trim() !== "");
  useEffect(() => {
    if (onDirtyChange) onDirtyChange(dirty);
  }, [dirty]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update flag input state for a given drone ID.
  const handleFlagInputChange = (id, value) => {
    setFlagInputs((prev) => ({ ...prev, [id]: value }));
  };

  // Helper: Returns the current flag description if one exists.
  const getCurrentFlag = (droneId) => {
    const existingFlags = flags.filter(
      (flag) => flag.drone_serial === droneId && flag.active
    );
    if (existingFlags.length > 0) {
      return existingFlags.map((flag) => flag.descript).join(", ");
    }
    return null;
  };

  // Process all drones with a new flag input.
  const handleSubmitFlags = async () => {
    setError("");

    // Validate user is logged in
    if (!user?.loggedin) {
      setError("User information unavailable. Please log in again.");
      return;
    }

    // Process only drones without an existing flag and with a non-empty input.
    const dronesToProcess = droneIds.filter(
      (id) => !getCurrentFlag(id) && (flagInputs[id] || "").trim() !== ""
    );
    if (dronesToProcess.length === 0) {
      setError("No new flags to add. Please input flag values for drones without an existing flag.");
      return;
    }

    // Confirmation dialog
    const confirmed = await confirm({
      message: `Are you sure you want to add flags to ${dronesToProcess.length} drone${dronesToProcess.length !== 1 ? 's' : ''}?`,
    });
    if (!confirmed) return;

    setIsProcessing(true);

    try {
      // Build array of drones with their flag descriptions
      const droneFlags = dronesToProcess.map(id => ({
        serialnumber: id,
        description: flagInputs[id].trim()
      }));

      // ONE atomic call - all succeed or all fail together (user_id from JWT)
      const result = await bulkAddFlags(api, droneFlags, "Flag");

      refreshFleet();
      toast.success(`Successfully added flags to ${result.processed} drones.`);
      closeModal();
    } catch (err) {
      console.error("Bulk flag add failed:", err);
      setError(err.message || "Bulk flag add failed. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const dronesWithoutFlags = droneIds.filter((id) => !getCurrentFlag(id));
  const dronesWithFlags = droneIds.filter((id) => getCurrentFlag(id));

  const rows = useMemo(() => droneIds.map((id, idx) => ({ id, idx })), [droneIds]);
  const tableMeta = useMemo(
    () => ({
      currentFlags: Object.fromEntries(droneIds.map((id) => [id, getCurrentFlag(id)])),
      inputs: flagInputs,
      onInput: handleFlagInputChange,
    }),
    // getCurrentFlag reads `flags`; the handler only sets state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [droneIds, flags, flagInputs],
  );

  return (
    <div className="flex flex-col max-h-[85vh]">
      {/* Header */}
      <div className="px-6 py-4 border-b border-outline-variant bg-yellow-900/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded bg-yellow-500/20 flex items-center justify-center text-yellow-400">
            <FlagIcon />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Bulk Flag Add</h1>
            <p className="text-on-surface-variant text-sm">{droneIds.length} drone{droneIds.length !== 1 ? 's' : ''} selected</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {droneIds.length > 0 ? (
          <div className="space-y-4">
            {/* Stats */}
            <div className="grid grid-cols-2 gap-3">
              <StatTile on="panel" label="Can Add Flag" value={dronesWithoutFlags.length} tone="accent" />
              <StatTile on="panel" label="Already Flagged" value={dronesWithFlags.length} />
            </div>

            <DataTable
              data={rows}
              columns={COLUMNS}
              meta={tableMeta}
              getRowId={getRowKey}
              on="panel"
              density="compact"
              maxHeight="16rem"
            />

            <Alert tone="error" show={!!error}>{error}</Alert>
          </div>
        ) : (
          <EmptyState variant="area">No drone IDs available.</EmptyState>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-outline-variant bg-surface-container flex justify-end gap-3">
        <button
          onClick={closeModal}
          className={buttonClass('quiet', 'md')}
        >
          Cancel
        </button>
        <button
          onClick={handleSubmitFlags}
          disabled={dronesWithoutFlags.length === 0 || isProcessing}
          aria-busy={isProcessing}
          className={buttonClass('filled', 'md')}
        >
          {isProcessing ? (
            <span className="flex items-center gap-2">
              <LogoSpinner size="inline" />
              Processing...
            </span>
          ) : (
            `Add ${dronesWithoutFlags.length} Flag${dronesWithoutFlags.length !== 1 ? 's' : ''}`
          )}
        </button>
      </div>
    </div>
  );
};

export default BulkFlagAdd;
